/**
 * Streaming Markdown Parser
 *
 * Lightweight markdown parsing for streaming content.
 * Supports headings, bold, italic, inline code, links, lists, and line breaks.
 */

import { isUrlSafe, sanitizeHtml, escapeHtml } from '../../ui/blocks/utils';

// =============================================================================
// MARKDOWN PARSING
// =============================================================================

/**
 * Core markdown parsing — processes line-by-line for block-level elements,
 * then applies inline formatting within each line.
 */
function parseMarkdownCore(text: string): string {
  const lines = text.split('\n');
  const outputLines: string[] = [];
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const trimmed = line.trim();

    // Headings: ## text
    const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      if (inList) { outputLines.push('</ul>'); inList = false; }
      const level = headingMatch[1]!.length;
      outputLines.push(`<h${level}>${inlineFormat(headingMatch[2]!)}</h${level}>`);
      continue;
    }

    // Horizontal rule: --- or ***
    if (/^[-*_]{3,}\s*$/.test(trimmed)) {
      if (inList) { outputLines.push('</ul>'); inList = false; }
      outputLines.push('<hr>');
      continue;
    }

    // Unordered list: - item or * item
    const listMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (listMatch) {
      if (!inList) { outputLines.push('<ul>'); inList = true; }
      outputLines.push(`<li>${inlineFormat(listMatch[1]!)}</li>`);
      continue;
    }

    // Not a list item — close open list
    if (inList && trimmed === '') {
      outputLines.push('</ul>');
      inList = false;
    } else if (inList && !listMatch) {
      outputLines.push('</ul>');
      inList = false;
    }

    // Blank line
    if (trimmed === '') {
      outputLines.push('<br>');
      continue;
    }

    // Regular text
    outputLines.push(inlineFormat(trimmed));
    // Add <br> between consecutive text lines (not after last line)
    if (i < lines.length - 1 && lines[i + 1]!.trim() !== '' && !lines[i + 1]!.trim().match(/^#{1,4}\s|^[-*]\s|^[-*_]{3,}/)) {
      outputLines.push('<br>');
    }
  }

  if (inList) { outputLines.push('</ul>'); }

  return outputLines.join('\n');
}

/**
 * Apply inline markdown formatting to a single line.
 * HTML is escaped first for safety, then formatting is applied.
 */
function inlineFormat(text: string): string {
  let html = escapeHtml(text);

  // Bold: **text**
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Italic: *text* (but not inside bold)
  html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');

  // Inline code: `code`
  html = html.replace(/`(.+?)`/g, '<code>$1</code>');

  // Links: [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, linkText, url) => {
    if (isUrlSafe(url)) {
      return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
    }
    return linkText;
  });

  return html;
}

/**
 * Parse markdown for final display (with DOMPurify sanitization).
 * Use this for completed content that won't change.
 */
export function parseMarkdown(text: string): string {
  const html = parseMarkdownCore(text);
  return sanitizeHtml(html);
}

/**
 * Parse markdown for streaming display.
 * Uses escapeHtml in inlineFormat for XSS protection.
 * DOMPurify sanitization happens later in finalizeContent().
 */
export function parseMarkdownStreaming(text: string): string {
  return parseMarkdownCore(text);
}
